import { useEffect, useMemo, useRef, useState } from "react";
import ModuleSelector from "../../components/ModuleSelector.jsx";
import MetricCard from "../../components/MetricCard.jsx";
import PlusUpgradeModal from "../../components/PlusUpgradeModal.jsx";
import { getPremiumVoiceOptionLabel, PREMIUM_VOICES } from "../../data/premiumVoices.js";
import { useI18n } from "../../i18n.jsx";
import { moduleJsonPath } from "../../lib/legacyPaths.js";
import { generatePremiumTts, isPremiumTtsConfigured, PREMIUM_TTS_MAX_CHARS } from "../../services/premiumTts.js";

const LANGUAGE = "en-US";
const PREMIUM_TTS_TEST_DAILY_LIMIT = 20;

const BROWSER_VOICE_PRIORITIES = [
  "Daniel",
  "Google UK English Female",
  "Google UK English Male",
  "Karen",
  "Microsoft Ryan",
  "Microsoft Sonia",
  "Samantha",
  "Google US English",
  "Microsoft Jenny",
  "Microsoft Aria",
  "Alex",
  "Victoria"
];

function getRecognitionCtor() {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function provideWords(text) {
  return text
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:?{}=\-_`~()]/g, "")
    .replace(/\s{2,}/g, " ")
    .split(" ")
    .filter(Boolean);
}

function provideWordsWithPunctuation(text) {
  return text.split(" ").filter(Boolean);
}

function safeDivision(x, y) {
  return y ? x / y : 0;
}

function syllableCount(word) {
  const exceptions = ["the", "be", "me", "she", "we"];
  if (exceptions.includes(word.toLowerCase())) return 1;
  const clean = word.toLowerCase().replace(/[.,/#!$%^&*;:?{}=\-_`~()]/g, "");
  if (!clean || !Number.isNaN(Number(clean))) return 0;
  const trimmed = clean.replace(/(?:[^laeiouy]|ed|[^laeiouy]e)$/, "").replace(/^y/, "");
  return (trimmed.match(/[aeiouy]{1,2}/g) || []).length;
}

function countSyllables(words) {
  return words.reduce((sum, word) => sum + syllableCount(word), 0);
}

function scorePronunciation(spokenText, targetText) {
  const spokenWords = provideWords(spokenText);
  const targetWords = provideWords(targetText);
  const displayWords = provideWordsWithPunctuation(targetText);
  const correctWords = [];

  const tokens = targetWords.map((word, index) => {
    const correct = spokenWords.includes(word);
    if (correct) correctWords.push({ index, word });
    return {
      text: displayWords[index] || word,
      correct
    };
  });

  return {
    accuracy: safeDivision(correctWords.length, targetWords.length),
    correctCount: correctWords.length,
    totalCount: targetWords.length,
    tokens
  };
}

function getFluencyAdvice(syllablesPerSecond, t) {
  if (syllablesPerSecond < 2) return t("pronunciation.fluencyTooSlow");
  if (syllablesPerSecond < 3.2) return t("pronunciation.fluencyGoodStart");
  if (syllablesPerSecond <= 5.9) return t("pronunciation.fluencyGreat");
  return t("pronunciation.fluencyTooFast");
}

function getAsrErrorMessage(errorType, t) {
  if (errorType === "network") {
    return t("asr.network");
  }
  if (errorType === "https-required") {
    return t("asr.httpsRequired");
  }
  if (errorType === "no-api") {
    return t("asr.noApi");
  }
  if (errorType === "not-allowed") {
    return t("asr.notAllowed");
  }
  if (errorType === "service-not-allowed") {
    return t("asr.serviceNotAllowed");
  }
  if (errorType === "audio-capture" || errorType === "no-speech") {
    return t("asr.audioCapture");
  }
  return t("asr.generic", { error: errorType });
}

function getPremiumFallbackMessage(error, t) {
  if (error?.code === "daily_limit") return t("premium.dailyLimit");
  if (error?.code === "hourly_limit") return t("premium.hourlyLimit");
  if (error?.code === "global_limit") return t("premium.globalLimit");
  if (error?.code === "too_long") return t("premium.tooLong", { count: PREMIUM_TTS_MAX_CHARS });
  if (error?.code === "not_configured") return t("premium.notConfigured");
  return t("premium.unavailable");
}

function isPremiumQuotaError(error) {
  return ["daily_limit", "hourly_limit", "global_limit"].includes(error?.code);
}

function getBrowserVoiceLabel(voice, t) {
  if (!voice) return t("pronunciation.deviceDefault");
  const accent = voice.lang === "en-GB" ? t("pronunciation.britishEnglish") : voice.lang === "en-US" ? t("pronunciation.americanEnglish") : voice.lang;
  return `${accent}: ${voice.name}`;
}

function getPreferredBrowserVoices(voices) {
  const uniqueVoices = voices.filter((voice, index, source) => source.findIndex((item) => item.name === voice.name) === index);
  const chosen = [];

  BROWSER_VOICE_PRIORITIES.forEach((preferredName) => {
    const match = uniqueVoices.find((voice) => voice.name.toLowerCase().includes(preferredName.toLowerCase()));
    if (match && !chosen.some((voice) => voice.name === match.name)) chosen.push(match);
  });

  uniqueVoices
    .filter((voice) => voice.lang === "en-US" || voice.lang === "en-GB")
    .forEach((voice) => {
      if (chosen.length < 5 && !chosen.some((item) => item.name === voice.name)) chosen.push(voice);
    });

  uniqueVoices.forEach((voice) => {
    if (chosen.length < 5 && !chosen.some((item) => item.name === voice.name)) chosen.push(voice);
  });

  return chosen.slice(0, 5);
}

function getDefaultBrowserVoiceName(voices) {
  const britishVoice = voices.find((voice) => voice.lang === "en-GB");
  return britishVoice?.name || voices[0]?.name || "";
}

export default function PronunciationPage({ level }) {
  const { t } = useI18n();
  const [moduleNumber, setModuleNumber] = useState(1);
  const [unitNumber, setUnitNumber] = useState(0);
  const [items, setItems] = useState([]);
  const [loadStatus, setLoadStatus] = useState(() => t("pronunciation.loadingData"));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [voices, setVoices] = useState([]);
  const [selectedVoiceName, setSelectedVoiceName] = useState("");
  const [premiumVoice, setPremiumVoice] = useState(PREMIUM_VOICES[0].id);
  const [speed, setSpeed] = useState(1);
  const [ttsStatus, setTtsStatus] = useState("");
  const [premiumStatus, setPremiumStatus] = useState("");
  const [premiumBusy, setPremiumBusy] = useState(false);
  const [premiumUpgradeOpen, setPremiumUpgradeOpen] = useState(false);
  const [asrSupported, setAsrSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [asrStatus, setAsrStatus] = useState("Ready");
  const [asrError, setAsrError] = useState("");
  const [transcript, setTranscript] = useState("");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [feedback, setFeedback] = useState(null);

  const recognitionRef = useRef(null);
  const transcriptRef = useRef("");
  const elapsedRef = useRef(0);
  const timerRef = useRef(null);
  const waitRef = useRef(null);
  const scoreTimeoutRef = useRef(null);
  const voiceDefaultedRef = useRef(false);
  const listeningRef = useRef(false);
  const soundContextRef = useRef(null);
  const premiumAudioRef = useRef(null);

  useEffect(() => {
    let ignore = false;
    setLoadStatus(t("pronunciation.loadingData"));
    fetch(moduleJsonPath(level.id, moduleNumber))
      .then((response) => {
        if (!response.ok) throw new Error(t("pronunciation.loadModuleError", { number: moduleNumber }));
        return response.json();
      })
      .then((data) => {
        if (ignore) return;
        setItems(data);
        setLoadStatus("");
      })
      .catch((error) => {
        if (ignore) return;
        setItems([]);
        setLoadStatus(error.message);
      });
    return () => {
      ignore = true;
    };
  }, [level.id, moduleNumber, t]);

  useEffect(() => {
    setAsrSupported(Boolean(getRecognitionCtor()));
  }, []);

  useEffect(() => {
    if (!("speechSynthesis" in window)) return undefined;

    function loadVoices() {
      const englishVoices = window.speechSynthesis
        .getVoices()
        .filter((voice) => voice.lang && voice.lang.toLowerCase().startsWith("en"));
      setVoices(englishVoices);
    }

    loadVoices();
    const timeout = window.setTimeout(loadVoices, 500);
    if (typeof window.speechSynthesis.addEventListener === "function") {
      window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    } else {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    return () => {
      window.clearTimeout(timeout);
      if (typeof window.speechSynthesis.removeEventListener === "function") {
        window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
      } else if (window.speechSynthesis.onvoiceschanged === loadVoices) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const filteredItems = useMemo(() => {
    if (!unitNumber) return items;
    return items.filter((item) => Number(item.lesson) === unitNumber);
  }, [items, unitNumber]);

  useEffect(() => {
    if (selectedIndex >= filteredItems.length) {
      setSelectedIndex(0);
    }
  }, [filteredItems.length, selectedIndex]);

  const current = filteredItems[selectedIndex] || filteredItems[0];
  const currentText = current?.speaking || "";
  const currentTarget = current?.speaking_target || current?.cItem || current?.mItem || "";
  const preferredBrowserVoices = useMemo(() => getPreferredBrowserVoices(voices), [voices]);
  const selectedVoice = preferredBrowserVoices.find((voice) => voice.name === selectedVoiceName) || voices.find((voice) => voice.name === selectedVoiceName) || null;
  const selectedPremiumVoice = PREMIUM_VOICES.find((voice) => voice.id === premiumVoice) || PREMIUM_VOICES[0];
  const targetWords = currentText ? provideWords(currentText).length : 0;

  useEffect(() => {
    if (voiceDefaultedRef.current || selectedVoiceName || preferredBrowserVoices.length === 0) return;
    const defaultVoiceName = getDefaultBrowserVoiceName(preferredBrowserVoices);
    if (defaultVoiceName) {
      voiceDefaultedRef.current = true;
      setSelectedVoiceName(defaultVoiceName);
    }
  }, [preferredBrowserVoices, selectedVoiceName]);

  function clearRecordingTimers() {
    window.clearInterval(timerRef.current);
    window.clearTimeout(waitRef.current);
    window.clearTimeout(scoreTimeoutRef.current);
    timerRef.current = null;
    waitRef.current = null;
    scoreTimeoutRef.current = null;
  }

  function stopRecognitionOnly() {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (!recognition) return;
    try {
      recognition.onend = null;
      recognition.onerror = null;
      recognition.onresult = null;
      recognition.stop();
    } catch {
      // Some browsers throw if recognition has already stopped.
    }
  }

  function playFeedbackSound(type) {
    if (!("AudioContext" in window || "webkitAudioContext" in window)) return;
    try {
      if (window.localStorage && window.localStorage.getItem("sielpaSound") === "off") return;
    } catch {
      // Ignore unavailable localStorage.
    }
    try {
      soundContextRef.current = soundContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = soundContextRef.current.createOscillator();
      const gain = soundContextRef.current.createGain();
      const now = soundContextRef.current.currentTime;
      const tones = { start: 440, success: 660, retry: 220 };
      oscillator.frequency.value = tones[type] || 440;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.045, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
      oscillator.connect(gain);
      gain.connect(soundContextRef.current.destination);
      oscillator.start(now);
      oscillator.stop(now + 0.17);
    } catch {
      // Feedback sound is optional.
    }
  }

  function resetRecordingControls(nextStatus = "Ready") {
    listeningRef.current = false;
    setIsListening(false);
    clearRecordingTimers();
    stopRecognitionOnly();
    setAsrStatus(nextStatus);
  }

  function handleAsrError(errorType) {
    resetRecordingControls("Ready");
    setAsrError(getAsrErrorMessage(errorType, t));
  }

  function scoreRecording() {
    const spoken = transcriptRef.current.trim();
    const score = scorePronunciation(spoken, currentText);
    const spokenWords = provideWords(spoken);
    const syllables = countSyllables(spokenWords);
    const seconds = elapsedRef.current || 0.5;
    const syllablesPerSecond = safeDivision(syllables, seconds);
    const nextFeedback = {
      ...score,
      transcript: spoken,
      elapsedSeconds: elapsedRef.current,
      syllablesPerSecond,
      fluencyAdvice: spokenWords.length ? getFluencyAdvice(syllablesPerSecond, t) : t("pronunciation.noFinalSpeech")
    };

    setFeedback(nextFeedback);
    setAsrStatus("Ready");
    playFeedbackSound(score.correctCount === score.totalCount ? "success" : "retry");
  }

  function createRecognition() {
    const RecognitionCtor = getRecognitionCtor();
    if (!RecognitionCtor) {
      handleAsrError("no-api");
      return null;
    }

    let recognition;
    try {
      recognition = new RecognitionCtor();
    } catch {
      handleAsrError("no-api");
      return null;
    }

    const isInternal = RecognitionCtor === window.SpeechRecognition;
    let networkErrorCount = 0;
    recognition.continuous = true;
    recognition.lang = LANGUAGE;
    recognition.interimResults = !isInternal;
    recognition.onerror = (event) => {
      if (event.error === "network") {
        networkErrorCount += 1;
        if (networkErrorCount < 3) {
          try {
            recognition.stop();
          } catch {
            // Already stopped.
          }
          window.setTimeout(() => {
            if (listeningRef.current) startRecognitionSafely(recognition);
          }, 500);
        } else {
          handleAsrError("network");
        }
        return;
      }
      handleAsrError(event.error);
    };
    recognition.onresult = (event) => {
      const results = event.results;
      for (let i = event.resultIndex; i < results.length; i += 1) {
        if (results[i].isFinal) {
          transcriptRef.current += `${results[i][0].transcript} `;
          setTranscript(transcriptRef.current.trim());
        }
      }
    };
    return recognition;
  }

  function startRecognitionSafely(recognition) {
    if (!recognition) return false;
    try {
      recognition.start();
      return true;
    } catch (error) {
      handleAsrError(error.name || "start-failed");
      return false;
    }
  }

  function startSpeaking() {
    if (!currentText) return;
    if (!asrSupported) {
      handleAsrError("no-api");
      return;
    }

    setAsrError("");
    setFeedback(null);
    setTranscript("");
    transcriptRef.current = "";
    elapsedRef.current = 0;
    setElapsedSeconds(0);
    setAsrStatus("Wait...");

    const recognition = createRecognition();
    recognitionRef.current = recognition;
    if (!recognition || !startRecognitionSafely(recognition)) {
      resetRecordingControls("Ready");
      return;
    }

    listeningRef.current = true;
    setIsListening(true);
    playFeedbackSound("start");
    waitRef.current = window.setTimeout(() => {
      if (!listeningRef.current) return;
      setAsrStatus("Listening...");
      timerRef.current = window.setInterval(() => {
        elapsedRef.current += 1;
        setElapsedSeconds(elapsedRef.current);
      }, 1000);
    }, 1000);
  }

  function stopSpeaking() {
    listeningRef.current = false;
    setIsListening(false);
    window.clearInterval(timerRef.current);
    window.clearTimeout(waitRef.current);
    timerRef.current = null;
    waitRef.current = null;
    const recognition = recognitionRef.current;
    if (recognition) {
      try {
        recognition.stop();
      } catch {
        // Some browsers throw if recognition has already stopped.
      }
    }
    setAsrStatus("Checking...");
    scoreTimeoutRef.current = window.setTimeout(() => {
      stopRecognitionOnly();
      scoreRecording();
    }, 900);
  }

  useEffect(() => () => {
    listeningRef.current = false;
    clearRecordingTimers();
    stopRecognitionOnly();
    if (premiumAudioRef.current) {
      premiumAudioRef.current.pause();
      premiumAudioRef.current = null;
    }
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  }, []);

  useEffect(() => {
    resetRecordingControls("Ready");
    setTranscript("");
    setFeedback(null);
    setAsrError("");
    transcriptRef.current = "";
    elapsedRef.current = 0;
    setElapsedSeconds(0);
  }, [currentText]);

  function hearBrowserAudio(nextStatus) {
    if (!currentText || !("speechSynthesis" in window)) {
      setTtsStatus(t("pronunciation.browserUnavailable"));
      return false;
    }

    const utter = new SpeechSynthesisUtterance();
    utter.lang = LANGUAGE;
    utter.rate = Number(speed) || 1;
    utter.voice = selectedVoice;
    utter.text = currentText;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
    setTtsStatus(nextStatus || t("pronunciation.browserPlaying"));
    return true;
  }

  async function hearPremiumAudio() {
    if (!currentText || premiumBusy) return;
    setPremiumStatus("");

    if (currentText.length > PREMIUM_TTS_MAX_CHARS) {
      hearBrowserAudio(t("pronunciation.browserPlayedInstead"));
      setPremiumStatus(t("vocab.premiumLimit", { count: PREMIUM_TTS_MAX_CHARS }));
      return;
    }

    if (!isPremiumTtsConfigured()) {
      hearBrowserAudio(t("pronunciation.browserPlayedInstead"));
      setPremiumStatus(t("pronunciation.premiumNeedsConfig"));
      return;
    }

    setPremiumBusy(true);
    setPremiumStatus(t("pronunciation.preparingPremium"));
    try {
      const result = await generatePremiumTts({
        text: currentText,
        voice: selectedPremiumVoice.id,
        accent: selectedPremiumVoice.accent,
        gender: selectedPremiumVoice.gender,
        voiceInstructions: selectedPremiumVoice.instructions,
        speed: Number(speed) || 1,
        requestedDailyLimit: PREMIUM_TTS_TEST_DAILY_LIMIT,
        language: LANGUAGE,
        source: "pronunciation_listen",
        level: level.id,
        tool: "pronunciation"
      });

      if (result.audioUrl) {
        if (premiumAudioRef.current) premiumAudioRef.current.pause();
        const audio = new Audio(result.audioUrl);
        premiumAudioRef.current = audio;
        await audio.play();
        const used = result.usedToday || 0;
        const limit = result.dailyLimit || PREMIUM_TTS_TEST_DAILY_LIMIT;
        const remaining = typeof result.remainingToday === "number" ? t("pronunciation.premiumRemaining", { count: result.remainingToday }) : "";
        setPremiumStatus(`${t("pronunciation.premiumPlayed", { voice: selectedPremiumVoice.label })} ${used}/${limit}.${remaining}`);
        setTtsStatus(t("pronunciation.premiumAudioPlaying"));
      } else {
        hearBrowserAudio(t("pronunciation.browserPlayedInstead"));
        setPremiumStatus(t("pronunciation.premiumNoAudio"));
      }
    } catch (error) {
      hearBrowserAudio(t("pronunciation.browserPlayedInstead"));
      setPremiumStatus(getPremiumFallbackMessage(error, t));
      if (isPremiumQuotaError(error)) setPremiumUpgradeOpen(true);
    } finally {
      setPremiumBusy(false);
    }
  }

  function goToPreviousLine() {
    setSelectedIndex((index) => Math.max(0, index - 1));
  }

  function goToNextLine() {
    setSelectedIndex((index) => Math.min(filteredItems.length - 1, index + 1));
  }

  useEffect(() => {
    function handleLineKeydown(event) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

      const target = event.target;
      if (target?.closest?.("input, select, textarea, button, [contenteditable='true']")) return;

      event.preventDefault();
      setSelectedIndex((index) => {
        if (event.key === "ArrowLeft") return Math.max(0, index - 1);
        return Math.min(filteredItems.length - 1, index + 1);
      });
    }

    window.addEventListener("keydown", handleLineKeydown);
    return () => window.removeEventListener("keydown", handleLineKeydown);
  }, [filteredItems.length]);

  const accuracyPercent = feedback ? (feedback.accuracy * 100).toFixed(1) : "0.0";
  const fluencyValue = feedback ? feedback.syllablesPerSecond.toFixed(2) : "0.00";
  const asrStatusText = {
    Ready: t("common.ready"),
    "Wait...": t("common.wait"),
    "Listening...": t("common.listening"),
    "Checking...": t("common.checking")
  };
  const browserStatusText = ttsStatus || (selectedVoice?.lang === "en-GB" ? t("pronunciation.britishReady") : t("pronunciation.browserReady"));
  const premiumStatusText = premiumStatus || t("pronunciation.premiumSelected", { voice: selectedPremiumVoice.label, accent: selectedPremiumVoice.accent });
  const speakStatusText = asrSupported ? (asrStatus === "Ready" ? t("pronunciation.speakInstruction") : asrStatusText[asrStatus] || asrStatus) : getAsrErrorMessage("no-api", t);
  const hasPreviousLine = selectedIndex > 0;
  const hasNextLine = selectedIndex < filteredItems.length - 1;

  return (
    <>
    <section className="pronunciation-workspace">
      <div className="setup-panel pronunciation-setup-card">
        <p className="kicker">{t("pronunciation.setupKicker", { level: level.label })}</p>
        <h2>{t("pronunciation.setupTitle")}</h2>
        <ModuleSelector
          level={level}
          moduleNumber={moduleNumber}
          unitNumber={unitNumber}
          onModuleChange={(value) => {
            setModuleNumber(value);
            setSelectedIndex(0);
          }}
          onUnitChange={(value) => {
            setUnitNumber(value);
            setSelectedIndex(0);
          }}
        />
        <div className="pronunciation-quick-stats" aria-label={t("pronunciation.statusLabel")}>
          <span>
            <strong>{filteredItems.length}</strong>
            {t("pronunciation.lines")}
          </span>
          <span>
            <strong>{targetWords}</strong>
            {t("common.words")}
          </span>
          <span>
            <strong>{isPremiumTtsConfigured() ? t("common.ready") : t("pronunciation.needsSetup")}</strong>
            Premium
          </span>
        </div>
        {loadStatus ? <p className="pronunciation-load-status" role="status">{loadStatus}</p> : null}
        <div className="pronunciation-control-grid">
          <label>
            <span className="field-label">{t("pronunciation.computerVoice")}</span>
            <select value={selectedVoiceName} onChange={(event) => setSelectedVoiceName(event.target.value)}>
              <option value="">{t("pronunciation.deviceDefault")}</option>
              {preferredBrowserVoices.map((voice) => (
                <option key={voice.name} value={voice.name}>
                  {getBrowserVoiceLabel(voice, t)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="field-label">{t("pronunciation.speed", { speed: Number(speed).toFixed(1) })}</span>
            <input
              className="range-input"
              type="range"
              min="0.7"
              max="1.3"
              step="0.1"
              value={speed}
              onChange={(event) => setSpeed(Number(event.target.value))}
            />
          </label>
        </div>
      </div>

      <article className="practice-card pronunciation-practice-card">
        <div className="pronunciation-card-header">
          <div>
            <p className="kicker">{t("pronunciation.currentLine")}</p>
            <h2>{currentText || t("pronunciation.chooseModule")}</h2>
          </div>
          <span>{currentTarget || t("pronunciation.speaking")}</span>
        </div>

        <div className="pronunciation-actions">
          <div className="pronunciation-action-buttons">
            <button className="primary-button" id="hear" type="button" onClick={() => hearBrowserAudio()} disabled={!currentText || isListening}>
              {t("pronunciation.hear")}
            </button>
            <button className="secondary-button premium-action" type="button" onClick={hearPremiumAudio} disabled={!currentText || isListening || premiumBusy}>
              {premiumBusy ? t("common.loading") : t("pronunciation.premiumHear")}
            </button>
            <button
              className={isListening ? "secondary-button stop-action" : "secondary-button"}
              type="button"
              onClick={isListening ? stopSpeaking : startSpeaking}
              disabled={!currentText || !asrSupported}
              title={!asrSupported ? getAsrErrorMessage("no-api", t) : undefined}
            >
              {isListening ? t("pronunciation.stopSpeaking") : t("pronunciation.speak")}
            </button>
          </div>
          <div className="pronunciation-premium-controls" aria-label={t("pronunciation.premiumVoiceOptions")}>
            <label>
              <span className="field-label">{t("common.premiumVoice")}</span>
              <select value={premiumVoice} onChange={(event) => setPremiumVoice(event.target.value)}>
                {PREMIUM_VOICES.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {getPremiumVoiceOptionLabel(voice)}
                  </option>
                ))}
              </select>
            </label>
            <button className="secondary-button plus-upgrade-inline" type="button" onClick={() => setPremiumUpgradeOpen(true)}>
              {t("plus.renewPlus")}
            </button>
          </div>
        </div>

        <div className="pronunciation-status-grid">
          <p>{browserStatusText}</p>
          <p>{premiumStatusText}</p>
          <p className={asrSupported ? "" : "is-warning"}>{speakStatusText}</p>
        </div>

        {asrError ? <div className="pronunciation-error" role="alert">{asrError}</div> : null}

        <div className="pronunciation-line-picker">
          <span className="field-label">{t("pronunciation.line")}</span>
          <div className="pronunciation-line-controls">
            <button className="secondary-button" type="button" onClick={goToPreviousLine} disabled={!hasPreviousLine} aria-keyshortcuts="ArrowLeft">
              {t("common.previous")}
            </button>
            <select value={selectedIndex} onChange={(event) => setSelectedIndex(Number(event.target.value))}>
              {filteredItems.map((item, index) => (
                <option key={`${item.speaking}-${index}`} value={index}>{item.speaking}</option>
              ))}
            </select>
            <button className="secondary-button" type="button" onClick={goToNextLine} disabled={!hasNextLine} aria-keyshortcuts="ArrowRight">
              {t("common.next")}
            </button>
          </div>
        </div>
      </article>

      <aside className="goals-card pronunciation-feedback-card">
        <div className="pronunciation-card-header">
          <div>
            <p className="kicker">{t("pronunciation.feedbackKicker")}</p>
            <h2>{t("pronunciation.result")}</h2>
          </div>
          <span>{elapsedSeconds}s</span>
        </div>

        <div className="metric-row pronunciation-metrics">
          <MetricCard label={t("pronunciation.accuracy")} value={`${accuracyPercent}%`} detail={feedback ? t("pronunciation.wordCount", { correct: feedback.correctCount, total: feedback.totalCount }) : t("pronunciation.speakToScore")} status={feedback && feedback.accuracy >= 0.8 ? "good" : feedback ? "needs-work" : "neutral"} />
          <MetricCard label={t("pronunciation.fluency")} value={fluencyValue} detail={t("pronunciation.syllablesSec")} status={feedback && feedback.syllablesPerSecond >= 2 && feedback.syllablesPerSecond <= 5.9 ? "good" : feedback ? "needs-work" : "neutral"} />
          <MetricCard label={t("pronunciation.captured")} value={provideWords(transcript).length} detail={transcript ? t("pronunciation.finalAsrWords") : t("pronunciation.waiting")} />
        </div>

        <p className="pronunciation-feedback-note">{t("pronunciation.missedRed")}</p>

        {feedback ? (
          <div className="pronunciation-word-feedback">
            <span className="field-label">{t("pronunciation.wordCheck")}</span>
            <p>
              {feedback.tokens.map((token, index) => (
                <span className={token.correct ? "word-ok" : "word-missed"} key={`${token.text}-${index}`}>
                  {token.text}
                </span>
              ))}
            </p>
            <small>{feedback.fluencyAdvice}</small>
          </div>
        ) : null}

        <div className="transcript-panel">
          <span className="field-label">{t("pronunciation.recognizedSpeech")}</span>
          <p>{transcript || t("pronunciation.transcriptPlaceholder")}</p>
        </div>
      </aside>
    </section>
    <PlusUpgradeModal open={premiumUpgradeOpen} onClose={() => setPremiumUpgradeOpen(false)} source="premium-voice-limit" />
    </>
  );
}
